import AutorenewIcon from "@mui/icons-material/Autorenew";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import {
  Box,
  Chip,
  Collapse,
  Stack,
  TableCell,
  TableRow,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { E2eTestCaseResult } from "services/KnowfullyAdminClient";

const statusConfig = {
  passed: { icon: <CheckCircleOutlineIcon fontSize="small" />, color: "success.main" },
  failed: { icon: <ErrorOutlineIcon fontSize="small" />, color: "error.main" },
  flaky: { icon: <AutorenewIcon fontSize="small" />, color: "warning.main" },
  skipped: { icon: <RemoveCircleOutlineIcon fontSize="small" />, color: "text.disabled" },
  unknown: { icon: <RemoveCircleOutlineIcon fontSize="small" />, color: "text.disabled" },
} as const;

type TestCaseRowProps = {
  testCase: E2eTestCaseResult;
};

const styles = {
  codeBlock: {
    fontFamily: "monospace",
    fontSize: "0.75rem",
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    overflowX: "auto",
    m: 0,
    p: 1,
    backgroundColor: "grey.900",
    color: "grey.100",
    borderRadius: 1,
  },
};

const TestCaseRow = ({ testCase }: TestCaseRowProps) => {
  const [errorOpen, setErrorOpen] = useState(false);
  const config = statusConfig[testCase.status as keyof typeof statusConfig] ?? statusConfig.unknown;
  const hasError = !!testCase.error;
  const durationSec = ((testCase.durationMs ?? 0) / 1000).toFixed(1);

  return (
    <>
      <TableRow
        hover={hasError}
        onClick={hasError ? () => setErrorOpen(!errorOpen) : undefined}
        sx={{ cursor: hasError ? "pointer" : "default" }}
      >
        <TableCell sx={{ width: 28, py: 0.5, color: config.color }}>
          {config.icon}
        </TableCell>
        <TableCell sx={{ py: 0.5 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            {testCase.koneTicket && (
              <Chip
                label={testCase.koneTicket}
                size="small"
                variant="outlined"
                sx={{ fontFamily: "monospace", fontSize: "0.7rem", height: 18 }}
              />
            )}
            <Typography variant="body2">{testCase.title}</Typography>
          </Stack>
        </TableCell>
        <TableCell align="right" sx={{ py: 0.5, whiteSpace: "nowrap" }}>
          <Stack direction="row" spacing={0.5} justifyContent="flex-end" alignItems="center">
            {(testCase.retryCount ?? 0) > 0 && (
              <Chip
                label={`↺${testCase.retryCount}`}
                size="small"
                color="warning"
                variant="outlined"
                sx={{ height: 18, fontSize: "0.7rem" }}
              />
            )}
            <Typography variant="caption" color="text.secondary">
              {durationSec}s
            </Typography>
          </Stack>
        </TableCell>
      </TableRow>
      {hasError && (
        <TableRow>
          <TableCell colSpan={3} sx={{ py: 0, borderBottom: errorOpen ? undefined : "none" }}>
            <Collapse in={errorOpen} unmountOnExit>
              <Box sx={{ px: 2, py: 1.5, backgroundColor: "error.50" }}>
                {testCase.error?.summary && (
                  <Typography
                    variant="caption"
                    component="pre"
                    sx={{
                      fontFamily: "monospace",
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-word",
                      color: "error.dark",
                      m: 0,
                      mb: testCase.error.details || testCase.error.snippet ? 1.5 : 0,
                    }}
                  >
                    {testCase.error.summary}
                  </Typography>
                )}
                {testCase.error?.details && (
                  <Box
                    component="pre"
                    sx={{ ...styles.codeBlock, mb: testCase.error.snippet ? 1.5 : 0 }}
                  >
                    {testCase.error.details}
                  </Box>
                )}
                {testCase.error?.snippet && (
                  <Box
                    component="pre"
                    sx={{
                      fontFamily: "monospace",
                      fontSize: "0.75rem",
                      whiteSpace: "pre",
                      overflowX: "auto",
                      m: 0,
                      p: 1,
                      backgroundColor: "grey.900",
                      color: "grey.100",
                      borderRadius: 1,
                    }}
                  >
                    {testCase.error.snippet}
                  </Box>
                )}
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  );
};

export default TestCaseRow;
